import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  CanActivate,
  CanLoad,
  Route,
  UrlSegment,
  CanActivateChild,
  UrlTree,
  Router,
} from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment as env } from 'src/environments/environment';
import { useAuth0Provider } from '../auth-mode';
import { AuthGuard } from '@auth0/auth0-angular';
import { firstValueFrom, Observable } from 'rxjs';

@Injectable()
export class PrAuthGuard implements CanActivate, CanLoad, CanActivateChild {
  protected authenticated = false;
  protected roles: string[] = [];

  constructor(
    protected router: Router,
    protected keycloakAngular: KeycloakService,
    private auth0AuthGuard: AuthGuard
  ) {}

  async isAccessAllowed(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (!this.authenticated && env.keycloak?.url) {
      await this.keycloakAngular.login({
        redirectUri: env.keycloak.redirectUri ?? window.location.origin + '/pr1core/',
      });
    }
    return this.authenticated;
  }

  canLoad(
    _route: Route,
    _segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    if (env.appMode === 'shopify') {
      return true;
    }
    if (useAuth0Provider()) {
      return this.auth0AuthGuard.canLoad(_route, _segments);
    }
    if (!env.keycloak?.url) {
      return true;
    }
    return this.canLoadKeycloak();
  }

  private async canLoadKeycloak(): Promise<boolean> {
    this.authenticated = await this.keycloakAngular.isLoggedIn();
    if (!this.authenticated) {
      await this.keycloakAngular.login({
        redirectUri: env.keycloak?.redirectUri ?? window.location.origin + '/pr1core/',
      });
      return false;
    }
    return true;
  }

  async canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (env.appMode === 'shopify') {
      return true;
    }
    return this.canActivate(childRoute, state);
  }

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean | UrlTree> {
    if (env.appMode === 'shopify') {
      return true;
    }
    if (useAuth0Provider()) {
      return firstValueFrom(this.auth0AuthGuard.canActivate(route, state));
    }
    if (!env.keycloak?.url) {
      return true;
    }
    try {
      this.authenticated = await this.keycloakAngular.isLoggedIn();
      this.roles = await this.keycloakAngular.getUserRoles(true);
      return await this.isAccessAllowed(route, state);
    } catch (error) {
      throw new Error('An error happened during access validation. Details:' + error);
    }
  }
}
